import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BookOpen, ShoppingBag, ChevronLeft, Lock, Heart, MessageCircle, Plus, X, Download, Mic } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";
import { Monogram } from "../components/Monogram";
import { UpgradeModal } from "../components/UpgradeModal";
import AnnualBookModal from "../components/AnnualBookModal";
import { FoundingBadge } from "../components/FoundingBadge";
import { LanguageSelector } from "../components/LanguageSelector";
import NotificationBell from "../components/NotificationBell";
import { useTranslation } from "react-i18next";
import { CHAPTERS, FAMILY_REQUEST_TOPICS } from "../data/content";

export default function ProfileHome() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [stories, setStories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [askingQ, setAskingQ] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [newQFrom, setNewQFrom] = useState("");
  const [memBusy, setMemBusy] = useState(false);
  const [annualOpen, setAnnualOpen] = useState(false);
  const [family, setFamily] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [plist, slist, meRes, qlist, famRes] = await Promise.all([
          api.get("/profiles"),
          api.get(`/stories/${id}`),
          api.get("/auth/me"),
          api.get(`/questions/${id}`).catch(() => ({ data: [] })),
          api.get("/families/me").catch(() => ({ data: null })),
        ]);
        const me = (plist.data || []).find((p) => p.profile_id === id);
        if (!me) { navigate("/dashboard"); return; }
        setProfile(me);
        setStories(slist.data || []);
        setMe(meRes.data);
        setQuestions(qlist.data || []);
        setFamily(famRes.data);
      } catch (e) { navigate("/dashboard"); } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const progress = useMemo(() => {
    const map = {};
    for (const s of stories) {
      const key = `${s.chapter_id}`;
      map[key] = map[key] || new Set();
      if ((s.answer_text || "").trim().length > 0) map[key].add(s.prompt_index);
    }
    return map;
  }, [stories]);

  if (loading || !profile || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center paper">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          Reflecting on your story…
        </p>
      </div>
    );
  }

  const tier = me.subscription_tier || "free";
  const isFamilyTier = tier === "family";
  const profileCount = me.profile_count || 0;
  const profileLimit = me.profile_limit; // null = unlimited
  const canAskQuestion = isFamilyTier;
  const canMemorial = tier === "family";

  const openChapter = (ch) => {
    if (tier === "free") { navigate("/subscribe"); return; }
    navigate(`/app/chapter/${profile.profile_id}/${ch.id}`);
  };

  const openRequest = (q) => {
    const ch = q.chapter_id || 1;
    navigate(`/app/chapter/${profile.profile_id}/${ch}?q=${q.question_id}`);
  };

  const toggleMemorial = async () => {
    if (!canMemorial) { setUpgradeReason("family_only"); setUpgradeOpen(true); return; }
    setMemBusy(true);
    try {
      const res = await api.patch(`/profiles/${profile.profile_id}`, { is_memorial: !profile.is_memorial });
      setProfile(res.data);
    } catch (e) { /* ignore */ } finally { setMemBusy(false); }
  };

  const submitQuestion = async (e, presetText = null) => {
    e?.preventDefault?.();
    if (!canAskQuestion) { setUpgradeReason("family_only"); setUpgradeOpen(true); return; }
    const text = (presetText || newQ).trim();
    if (!text) return;
    try {
      const res = await api.post("/questions", {
        profile_id: profile.profile_id,
        question_text: text,
        from_name: newQFrom.trim() || null,
      });
      setQuestions((qs) => [res.data, ...qs]);
      setNewQ(""); setNewQFrom(""); setAskingQ(false);
    } catch (e) {
      if (e?.response?.status === 402) { setUpgradeReason("family_only"); setUpgradeOpen(true); setAskingQ(false); }
    }
  };

  const pickTopic = (topic) => {
    submitQuestion(null, topic.text);
  };

  const dismissQuestion = async (qid) => {
    try {
      await api.delete(`/questions/${qid}`);
      setQuestions((qs) => qs.filter((q) => q.question_id !== qid));
    } catch (e) { /* ignore */ }
  };

  const pendingQs = questions.filter((q) => q.status === "pending");

  return (
    <div className="min-h-screen paper" data-testid="profile-home-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-to-dashboard">
          <ChevronLeft size={16} /> {t("common.back")}
        </Link>
        <div className="flex items-center gap-5">
          {false && <LanguageSelector tone="light" />}
          <NotificationBell />
          <div className="flex items-center gap-3">
            <span className="text-[#B8973A] text-lg" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
            <span style={{ fontFamily: "Cormorant Garamond, serif", letterSpacing: "0.06em" }}>The Living Book</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        {/* Requests from the Family — hero position, click to record */}
        {pendingQs.length > 0 && (
          <div className="mb-12" data-testid="requests-from-family">
            <div className="flex items-baseline justify-between mb-5">
              <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]" data-testid="requests-heading">
                {t("profile.requestsFromFamily", "REQUESTS FROM THE FAMILY")}
              </p>
              <span className="label-ui text-[10px] text-[#8C8278]" data-testid="requests-count">
                {t("profile.requestsCount", { count: pendingQs.length, defaultValue: "{{count}} waiting" })}
              </span>
            </div>
            <p className="text-[#8C8278] italic mb-6" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
              {t("profile.requestsHint", "Tap a question to open the recording screen — your family is listening.")}
            </p>
            <div className="space-y-3">
              {pendingQs.map((q) => (
                <div
                  key={q.question_id}
                  className="group p-6 bg-white hairline-cream flex items-start justify-between gap-4 hover:bg-[#FDF9F1] hover:border-[#B8973A] transition-colors cursor-pointer"
                  style={{ borderRadius: 4 }}
                  onClick={() => openRequest(q)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openRequest(q); } }}
                  data-testid={`request-card-${q.question_id}`}
                >
                  <div className="flex-1">
                    <p className="text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "1.35rem", lineHeight: 1.5 }} data-testid={`request-text-${q.question_id}`}>
                      &ldquo;{q.question_text}&rdquo;
                    </p>
                    <p className="label-ui text-[10px] text-[#B8973A] mt-3" data-testid={`request-from-${q.question_id}`}>
                      — {q.from_name || t("profile.yourFamily", "Your family")}
                    </p>
                    <p className="label-ui text-[10px] text-[#8C8278] mt-3 flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                      <Mic size={11} /> {t("profile.tapToAnswer", "Tap to open the recording screen")}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissQuestion(q.question_id); }}
                    className="text-[#8C8278] hover:text-[#111D33] transition-colors"
                    aria-label={t("common.dismiss", "Dismiss")}
                    data-testid={`dismiss-${q.question_id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mb-16">
          <div className="inline-block"><Monogram name={profile.name} size={80} /></div>
          {profile.is_memorial && (
            <p className="label-ui text-[10px] text-[#B8973A] mt-4 flex items-center justify-center gap-2" data-testid="memorial-indicator">
              <Heart size={11} fill="#B8973A" /> {t("profile.inLovingMemory")}
            </p>
          )}
          <p className="label-ui text-[11px] text-[#B8973A] mt-3">{t(`relations.${profile.relation}`, profile.relation)}</p>
          <h1 className="text-5xl sm:text-6xl mt-2" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300 }} data-testid="profile-name">{profile.name}</h1>
          <FoundingBadge active={!!me?.is_founding_member} className="mt-4" />
          <Ornament className="my-8" />
          <p className="text-[#8C8278] italic max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
            {t("profile.chooseChapter")}
          </p>
          <p className="mt-6 text-[#111D33] italic max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }} data-testid="profile-manifesto">
            &ldquo;Every great family story begins with one question.&rdquo;
          </p>
        </div>

        {tier === "free" && (
          <div className="mb-12" data-testid="story-continues-here">
            <div className="p-8 sm:p-10 bg-[#111D33] text-[#FAF7F2]" style={{ borderRadius: 4 }}>
              <p className="label-ui text-[11px] text-[#B8973A] mb-4 tracking-[0.3em]">THE STORY CONTINUES HERE</p>
              <h3 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.2 }}>
                Your seven pages are here whenever you want them.
                <br/>
                <span style={{ fontStyle: "italic", color: "#E8D5A3" }}>The other fourteen questions open when you become a Story Keeper.</span>
              </h3>
              <p className="italic mb-8" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.6, color: "#E8D5A3", maxWidth: "42rem" }}>
                Ongoing voice recording. Photos on every memory. AI follow-ups that draw out the detail.
                Family participation. A printed hardback with a QR code beside every story that plays their voice.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 max-w-2xl">
                {[
                  { l: "VOICE", v: "Unlimited" },
                  { l: "PHOTOS", v: "Every page" },
                  { l: "AI FOLLOW-UPS", v: "Unlimited" },
                  { l: "PRINTED BOOK", v: "Yours to order" },
                ].map((it) => (
                  <div key={it.l}>
                    <p className="label-ui text-[10px] text-[#B8973A] mb-1 tracking-[0.2em]">{it.l}</p>
                    <p style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>{it.v}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => navigate("/subscribe")} className="btn-gold" data-testid="become-founding-btn">
                  Become a Story Keeper
                </button>
                <button onClick={() => navigate(`/app/stories/${profile.profile_id}`)} className="label-ui text-[11px] text-[#E8D5A3] hover:text-white underline-offset-8 hover:underline transition-colors px-4 py-3" data-testid="view-story-week-btn">
                  Read my seven responses
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ask a question — composer with topic chips (Family tier only) */}
        <div className="mb-12" data-testid="ask-question-container">
          {askingQ ? (
            <form onSubmit={submitQuestion} className="max-w-2xl mx-auto bg-white hairline-cream p-6 sm:p-8" style={{ borderRadius: 4 }} data-testid="ask-question-form">
              <p className="label-ui text-[10px] text-[#B8973A] mb-4 tracking-[0.25em]">
                {t("profile.askHeading", "REQUEST A STORY FROM {{name}}", { name: profile.name })}
              </p>

              {/* Suggested topic chips */}
              <p className="label-ui text-[10px] text-[#8C8278] mb-3">{t("profile.pickATopic", "Pick a topic — or write your own below")}</p>
              <div className="flex flex-wrap gap-2 mb-5" data-testid="topic-chips">
                {FAMILY_REQUEST_TOPICS.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => pickTopic(topic)}
                    className="px-4 py-2 hairline-cream text-[#1B2A4A] hover:bg-[#F7F0E0] hover:border-[#B8973A] transition-colors text-sm"
                    style={{ borderRadius: 999, fontFamily: "Georgia, serif" }}
                    data-testid={`topic-chip-${topic.id}`}
                  >
                    {t(`topics.${topic.id}`, topic.label)}
                  </button>
                ))}
              </div>

              <div className="h-px w-full bg-[#F0EAE0] my-5" />

              <input value={newQFrom} onChange={(e) => setNewQFrom(e.target.value)} placeholder={t("profile.signedAs", "Signed (optional) — e.g. 'Your granddaughter'")}
                className="w-full px-4 py-3 hairline-cream bg-white mb-3" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }}
                data-testid="question-from-input" />
              <textarea value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder={t("profile.questionPlaceholder", "What do you wish you'd asked them?")} rows={3}
                className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.6 }}
                data-testid="question-text-input" />
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => { setAskingQ(false); setNewQ(""); setNewQFrom(""); }} className="btn-outline-navy flex-1" data-testid="cancel-question-btn">{t("common.cancel", "Cancel")}</button>
                <button type="submit" className="btn-primary flex-1" data-testid="submit-question-btn">{t("profile.sendRequest", "Send request")}</button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <button
                onClick={() => canAskQuestion ? setAskingQ(true) : (setUpgradeReason("family_only"), setUpgradeOpen(true))}
                className="btn-outline-navy inline-flex items-center gap-2"
                data-testid="add-question-btn"
              >
                <MessageCircle size={16} /> {t("profile.requestAStory", "Request a story from {{name}}", { name: profile.name })}
                {!canAskQuestion && <Lock size={13} className="ml-1" />}
              </button>
              {!canAskQuestion && (
                <p className="label-ui text-[10px] text-[#8C8278] mt-3" data-testid="request-family-only-hint">
                  {t("profile.familyTierOnly", "Available on The Living Book Family plan")}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Chapters — all 7 open to every user */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#F0EAE0] mb-12">
          {CHAPTERS.map((ch) => {
            const done = (progress[ch.id]?.size) || 0;
            const total = ch.prompts.length;
            const complete = done >= total;
            return (
              <button
                key={ch.id} onClick={() => openChapter(ch)}
                className="text-left p-8 transition-colors duration-500 bg-[#FAF7F2] hover:bg-[#F7F0E0]"
                data-testid={`chapter-card-${ch.id}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="chapter-roman">{ch.roman}</span>
                  <span className="label-ui text-[10px] text-[#8C8278]">{t("profile.ofTotal", { done, total })}{complete ? ` · ${t("common.complete")}` : ""}</span>
                </div>
                <h3 className="text-3xl mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{ch.title}</h3>
                <p className="text-[#8C8278] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>{ch.subtitle}</p>
                <div className="mt-6 h-px w-full bg-[#F0EAE0]">
                  <div className="h-px bg-[#B8973A] transition-all duration-500" style={{ width: `${(done / total) * 100}%` }} />
                </div>
                {complete && (
                  <p className="label-ui text-[10px] text-[#B8973A] mt-4">{t("profile.alwaysOpen")}</p>
                )}
              </button>
            );
          })}
        </div>

        {/* Memorial toggle (Family tier) */}
        <div className="text-center mb-10">
          <button onClick={toggleMemorial} disabled={memBusy}
            className="label-ui text-[11px] text-[#8C8278] hover:text-[#B8973A] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="memorial-toggle-btn">
            <Heart size={13} />
            {profile.is_memorial ? "Remove memorial status" : "Mark as in memory"}
            {!canMemorial && <Lock size={11} className="ml-1" />}
          </button>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button onClick={() => navigate(`/app/stories/${profile.profile_id}`)} className="btn-outline-navy flex items-center justify-center gap-2" data-testid="view-stories-btn">
            <BookOpen size={16} /> Stories so far
          </button>
          <button onClick={() => navigate(`/app/book/${profile.profile_id}`)} className="btn-outline-navy flex items-center justify-center gap-2" data-testid="preview-book-btn">
            <BookOpen size={16} /> Preview the book
          </button>
          <button onClick={() => setAnnualOpen(true)} className="btn-outline-navy flex items-center justify-center gap-2" data-testid="annual-book-btn">
            <Download size={16} /> Annual PDF
          </button>
          <button onClick={() => navigate(`/app/order/${profile.profile_id}`)} className="btn-gold flex items-center justify-center gap-2" data-testid="order-book-btn">
            <ShoppingBag size={16} /> Order the book
          </button>
        </div>

        {/* The Soundtrack of Your Life */}
        <button
          onClick={() => navigate(`/app/soundtrack/${profile.profile_id}`)}
          className="mt-6 w-full p-6 bg-white hairline-cream hover:bg-[#FDF9F1] transition-colors text-left flex items-center justify-between gap-4"
          style={{ borderRadius: 4 }}
          data-testid="soundtrack-nav-btn"
        >
          <div>
            <p className="label-ui text-[10px] text-[#B8973A] mb-1 tracking-[0.25em]">THE SOUNDTRACK OF YOUR LIFE</p>
            <p className="text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.35rem", lineHeight: 1.4 }}>
              The songs that <span style={{ fontStyle: "italic", color: "#B8973A" }}>shaped you</span>, saved beside your words.
            </p>
          </div>
          <span className="label-ui text-[11px] text-[#B8973A]">OPEN →</span>
        </button>
        {/* Time Capsule promo */}
        <button
          onClick={() => navigate(`/app/capsules/${profile.profile_id}`)}
          className="mt-6 w-full p-6 bg-[#111D33] hover:bg-[#1B2A4A] transition-colors text-left flex items-center justify-between gap-4"
          style={{ borderRadius: 4 }}
          data-testid="time-capsule-btn"
        >
          <div>
            <p className="label-ui text-[10px] text-[#B8973A] mb-1 tracking-[0.25em]">FAMILY TIME CAPSULE</p>
            <p className="text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.35rem", lineHeight: 1.4 }}>
              Leave a message that opens on a <span style={{ fontStyle: "italic", color: "#E8D5A3" }}>21st birthday, a wedding day, a moment yet to come.</span>
            </p>
          </div>
          <div className="text-[#B8973A] text-2xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</div>
        </button>
      </main>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
      <AnnualBookModal open={annualOpen} onClose={() => setAnnualOpen(false)} stories={stories} profile={profile} family={family} />
    </div>
  );
}
